// apps/client/src/components/BottomNav.tsx

import { useEffect, useRef, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { getAvatarColor } from "../lib/avatar-color";

const API_BASE = import.meta.env.VITE_API_URL ?? "";

const PAGE_TITLES: Record<string, string> = {
  "/": "Agenda",
  "/dichiara": "Dichiara",
  "/profile": "Profilo",
  "/settings": "Impostazioni",
  "/admin": "Admin",
};

// Icone outline thin — stroke-width 1.5, linecap round, linejoin round

const AgendaIcon = () => (
  <svg
    className="w-5 h-5"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    viewBox="0 0 24 24"
  >
    <rect x="3" y="4" width="18" height="18" rx="2" />
    <path d="M16 2v4M8 2v4M3 10h18" />
  </svg>
);

const PrenotaIcon = () => (
  <svg
    className="w-5 h-5"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    viewBox="0 0 24 24"
  >
    <rect x="3" y="4" width="18" height="18" rx="2" />
    <path d="M16 2v4M8 2v4M3 10h18" />
    <path d="M12 14v4M10 16h4" />
  </svg>
);

const AdminIcon = () => (
  <svg
    className="w-5 h-5"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    viewBox="0 0 24 24"
  >
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z" />
  </svg>
);

type Tab = { to: string; label: string; icon: React.ReactElement; tourTarget?: string };

const baseTabs: Tab[] = [
  { to: "/", label: "Agenda", icon: <AgendaIcon />, tourTarget: "agenda" },
  { to: "/dichiara", label: "Dichiara", icon: <PrenotaIcon />, tourTarget: "prenota" },
];

const adminTab: Tab = { to: "/admin", label: "Admin", icon: <AdminIcon /> };

export function BottomNav() {
  const { user } = useAuth();
  const location = useLocation();
  const tabs = user?.role === "admin" ? [...baseTabs, adminTab] : baseTabs;

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const [avatarError, setAvatarError] = useState(false);
  const pageTitle = PAGE_TITLES[location.pathname] ?? "WorkIn";
  const initials =
    user?.name
      ?.split(" ")
      .map((p) => p[0])
      .slice(0, 2)
      .join("")
      .toUpperCase() ?? "";
  const avatarColors = user ? getAvatarColor(user.id) : { bg: "bg-blue-100", text: "text-blue-700" };

  return (
    <>
      {/* Mobile: top bar — avatar dropdown */}
      <header className="md:hidden fixed top-0 left-0 right-0 bg-white border-b border-gray-200 z-10 h-14 flex items-center justify-between px-4">
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 bg-blue-600 rounded-lg flex items-center justify-center">
            <span className="text-white font-bold text-xs">W</span>
          </div>
          <span className="font-semibold text-gray-900">{pageTitle}</span>
        </div>
        <div className="relative" ref={dropdownRef}>
          <button onClick={() => setDropdownOpen((v) => !v)} className="focus:outline-none" aria-label="Menu utente">
            {user?.avatarUrl && !avatarError ? (
              <img
                src={user.avatarUrl}
                alt={user.name ?? ""}
                className="w-8 h-8 rounded-full object-cover"
                onError={() => setAvatarError(true)}
              />
            ) : (
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold ${avatarColors.bg} ${avatarColors.text}`}
              >
                {initials}
              </div>
            )}
          </button>
          {dropdownOpen && (
            <div className="absolute right-0 top-10 w-48 bg-white rounded-2xl shadow-lg border border-gray-100 py-1 z-50">
              <div className="px-4 py-2 border-b border-gray-100">
                <p className="text-sm font-semibold text-gray-900 truncate">{user?.name}</p>
                <p className="text-xs text-gray-400 truncate">{user?.email}</p>
              </div>
              <NavLink
                to="/profile"
                onClick={() => setDropdownOpen(false)}
                className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Profilo
              </NavLink>
              <NavLink
                to="/settings"
                onClick={() => setDropdownOpen(false)}
                className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Impostazioni
              </NavLink>
              <div className="border-t border-gray-100 mt-1">
                <a
                  href={`${API_BASE}/api/auth/logout`}
                  onClick={() => {
                    localStorage.removeItem("auth_token");
                    setDropdownOpen(false);
                  }}
                  className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-red-500 hover:bg-red-50 transition-colors"
                >
                  Esci
                </a>
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Mobile: bottom tabs */}
      <nav
        className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-10"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      >
        <div className="flex justify-around items-center h-16 max-w-lg mx-auto px-2">
          {tabs.map((tab) => (
            <NavLink
              key={tab.to}
              to={tab.to}
              end={tab.to === "/"}
              data-tour-target={tab.tourTarget}
              className={({ isActive }) =>
                `flex flex-col items-center gap-0.5 px-3 py-2 rounded-xl transition-colors ${
                  isActive ? "text-blue-600" : "text-gray-400 hover:text-gray-600"
                }`
              }
            >
              <div className="relative inline-flex">{tab.icon}</div>
              <span className="text-xs font-medium">{tab.label}</span>
            </NavLink>
          ))}
        </div>
      </nav>

      {/* Desktop: left sidebar */}
      <nav className="hidden md:flex fixed top-0 left-0 bottom-0 w-56 bg-white border-r border-gray-200 z-10 flex-col px-3 py-6">
        <div className="flex items-center gap-2 px-3 mb-8">
          <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center shrink-0">
            <span className="text-white font-bold text-sm">W</span>
          </div>
          <div>
            <span className="font-bold text-gray-900 leading-none">{import.meta.env.VITE_APP_NAME ?? "WorkIn"}</span>
            <p className="text-xs text-gray-400 leading-none mt-0.5">v{__APP_VERSION__}</p>
          </div>
        </div>
        <div className="flex flex-col gap-1 flex-1">
          {tabs.map((tab) => (
            <NavLink
              key={tab.to}
              to={tab.to}
              end={tab.to === "/"}
              data-tour-target={tab.tourTarget}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors text-sm font-medium ${
                  isActive ? "bg-blue-50 text-blue-600" : "text-gray-500 hover:bg-gray-50 hover:text-gray-900"
                }`
              }
            >
              <div className="relative inline-flex shrink-0">{tab.icon}</div>
              {tab.label}
            </NavLink>
          ))}
        </div>
        {/* Profilo e Impostazioni in fondo alla sidebar */}
        <div className="border-t border-gray-100 pt-3 flex flex-col gap-1">
          <NavLink
            to="/profile"
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors ${
                isActive ? "bg-blue-50 text-blue-600" : "text-gray-500 hover:bg-gray-50 hover:text-gray-900"
              }`
            }
          >
            {user?.avatarUrl && !avatarError ? (
              <img
                src={user.avatarUrl}
                alt={user.name ?? ""}
                className="w-6 h-6 rounded-full object-cover shrink-0"
                onError={() => setAvatarError(true)}
              />
            ) : (
              <div
                className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold shrink-0 ${avatarColors.bg} ${avatarColors.text}`}
              >
                {initials}
              </div>
            )}
            <span className="text-sm font-medium truncate">{user?.name ?? "Profilo"}</span>
          </NavLink>
          <NavLink
            to="/settings"
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors text-sm font-medium ${
                isActive ? "bg-blue-50 text-blue-600" : "text-gray-500 hover:bg-gray-50 hover:text-gray-900"
              }`
            }
          >
            {/* gear icon */}
            <svg
              className="w-5 h-5 shrink-0"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              viewBox="0 0 24 24"
            >
              <circle cx="12" cy="12" r="3" />
              <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z" />
            </svg>
            Impostazioni
          </NavLink>
        </div>
      </nav>
    </>
  );
}
